/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Router,
  NavigationEnd,
  RouterLinkActive,
  RouterLink,
} from '@angular/router';
import { DOCUMENT, NgClass } from '@angular/common';
import {
  Component,
  Inject,
  ElementRef,
  OnInit,
  AfterViewInit,
  Renderer2,
  HostListener,
  OnDestroy,
  NgZone,
  ChangeDetectorRef,
} from '@angular/core';
import { ROUTES } from './sidebar-items';
import { AuthService, UserRole } from '@core';
import { RouteInfo } from './sidebar.metadata';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FeatherModule } from 'angular-feather';
import { NgScrollbar } from 'ngx-scrollbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UnsubscribeOnDestroyAdapter } from '@shared';
import { EnterprisePeriodComponent } from "../enterprise-period/enterprise-period.component";
import { CompanySelectionService } from '@core/service/company-selection.service';
import { EnterpriseSelectionService } from '@core/service/enterprise-selection.service';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { EnterpriseConfigService } from '@core/service/enterprise-config.service';
import { PlanUsageService } from '@core/service/plan-usage.service';
@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: true,
  imports: [
    NgScrollbar,
    RouterLinkActive,
    RouterLink,
    NgClass,
    FeatherModule,
    TranslateModule,
    EnterprisePeriodComponent,
    MatTooltipModule
],
})
export class SidebarComponent
  extends UnsubscribeOnDestroyAdapter
  implements OnInit, AfterViewInit
{
  public sidebarItems: RouteInfo[] = [];
  public innerHeight?: number;
  public bodyTag!: HTMLElement;
  listMaxHeight?: string;
  listMaxWidth?: string;
  userFullName?: string;
  userImg?: string;
  userType?: UserRole;
  headerHeight = 60;
  currentRoute?: string;
  selectedEnterprise!: string;
  selectedCompanyId: string = '';
  private currentUserRole: UserRole | null = null;

  // Flyout submenu properties
  showFlyout = false;
  flyoutItem: RouteInfo | null = null;
  flyoutTop = 0;
  isCollapsed = false;

  // Collapsible section state (keyed by groupTitle translation key)
  collapsedSections: { [key: string]: boolean } = {};

  toggleSection(titleKey: string): void {
    this.collapsedSections[titleKey] = !this.collapsedSections[titleKey];
  }

  isSectionCollapsed(titleKey: string): boolean {
    if (this.isCollapsed) return false;
    return !!this.collapsedSections[titleKey];
  }

  isItemInSection(itemIndex: number): boolean {
    // Find the groupTitle that precedes this item
    for (let i = itemIndex - 1; i >= 0; i--) {
      const item = this.sidebarItems[i];
      if (item.groupTitle) {
        return this.isSectionCollapsed(item.title);
      }
    }
    return false;
  }
  private flyoutTimeout: ReturnType<typeof setTimeout> | null = null;
  private bodyClassObserver: MutationObserver | null = null;
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    public elementRef: ElementRef,
    private authService: AuthService,
    private enterpriseConfigService: EnterpriseConfigService,
    private planUsageService: PlanUsageService,
    @Inject(Router) private router: Router,
    private companySelectionService: CompanySelectionService,
    private enterpriseSelectionService: EnterpriseSelectionService,
    private translateService: TranslateService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {
    super();
    this.elementRef.nativeElement.closest('body');
    this.subs.sink = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // close sidebar on mobile screen after menu select
        this.renderer.removeClass(this.document.body, 'overlay-open');
      }
    });
  }
  @HostListener('window:resize')
  windowResizecall() {
    this.setMenuHeight();
    this.checkStatuForResize(false);
  }
  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.renderer.removeClass(this.document.body, 'overlay-open');
    }
  }
  callToggleMenu(event: Event, length: number) {
    if (length > 0) {
      const parentElement = (event.target as HTMLInputElement).closest('li');
      const activeClass = parentElement?.classList.contains('active');

      if (activeClass) {
        this.renderer.removeClass(parentElement, 'active');
      } else {
        this.renderer.addClass(parentElement, 'active');
      }
    }
  }
  private applyUserRole(userRole: UserRole): void {
    this.currentUserRole = userRole;
    this.rebuildSidebarItems();
    if (userRole === UserRole.SuperAdmin) {
      this.userType = UserRole.SuperAdmin;
    } else if (userRole === UserRole.PartnerAdmin) {
      this.userType = UserRole.PartnerAdmin;
    } else if (userRole === UserRole.Admin) {
      this.userType = UserRole.Admin;
    } else if (userRole === UserRole.CompanyAdmin) {
      this.userType = UserRole.CompanyAdmin;
    } else if (userRole === UserRole.EnterpriseAdmin) {
      this.userType = UserRole.EnterpriseAdmin;
    } else {
      this.userType = UserRole.EnterpriseAnalyst;
    }
  }

  private rebuildSidebarItems(): void {
    if (this.currentUserRole === null) return;
    const userRole = this.currentUserRole;
    const byRole = ROUTES.filter(
      (x) => x.role.indexOf(userRole) !== -1 || x.role.indexOf(UserRole.All) !== -1
    );
    let currentSection = '';
    this.sidebarItems = byRole.filter(item => {
      if (item.groupTitle) {
        currentSection = item.title;
        if (!(item.title in this.collapsedSections)) {
          this.collapsedSections[item.title] = true;
        }
      }
      // Company section (Companhia) only shows when a company is selected
      if (currentSection === 'MODELS.COMPANY.TEXT' && !this.selectedCompanyId) {
        return false;
      }
      // Enterprise section (Empresa) only shows when an enterprise is selected
      if (currentSection === 'MODELS.ENTERPRISE.TEXT' && !this.selectedEnterprise) {
        return false;
      }
      return true;
    });
  }

  ngOnInit() {
    // Subscribe to currentUser observable so sidebar rebuilds if user resolves after Keycloak init
    this.subs.sink = this.authService.currentUser.subscribe((user) => {
      if (!user?.role && user?.role !== 0) return;
      this.userFullName = (user.firstName || '') + ' ' + (user.lastName || '');
      this.userImg = user.img;
      this.applyUserRole(user.role);
      this.cdr.detectChanges();
      this.loadSubmenuItems();
    });

    this.companySelectionService.selectedCompany$.subscribe((companyId) => {
      this.selectedCompanyId = companyId || '';
      this.rebuildSidebarItems();
      this.loadSubmenuItems();
      this.cdr.detectChanges();
    });

    this.enterpriseSelectionService.selectedEnterprise$.subscribe((enterpriseId) => {
      this.selectedEnterprise = enterpriseId || '';
      this.rebuildSidebarItems();
      this.loadSubmenuItems();
      this.cdr.detectChanges();
    });

    // this.sidebarItems = ROUTES.filter((sidebarItem) => sidebarItem);
    this.initLeftSidebar();
    this.bodyTag = this.document.body;
  }

  ngAfterViewInit(): void {
    // Schedule state update after initial change detection to avoid NG0100
    setTimeout(() => {
      this.updateCollapsedState();
      this.cdr.detectChanges();
    }, 0);
    
    // Set up MutationObserver to track body class changes
    this.bodyClassObserver = new MutationObserver((mutations) => {
      this.ngZone.run(() => {
        this.updateCollapsedState();
      });
    });
    
    this.bodyClassObserver.observe(this.document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  private updateCollapsedState(): void {
    this.isCollapsed = this.document.body.classList.contains('side-closed');
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.bodyClassObserver) {
      this.bodyClassObserver.disconnect();
    }
  }

  loadSubmenuItems(): void {
    const classificationsMenuIndex = ROUTES.findIndex(
      (route) => route.title === 'MENUITEMS.CLASSIFICATIONS.TEXT'
    );
    
    const operationsMenuIndex = ROUTES.findIndex(
      (route) => route.title === 'MENUITEMS.OPERATION.LIST'
    );

    const reportsMenuIndex = ROUTES.findIndex(
      (route) => route.title === 'MENUITEMS.REPORT.LIST'
    );

    const exportMenuIndex = ROUTES.findIndex(
      (route) => route.title === 'MENUITEMS.EXPORT.TEXT'
    );

    if (localStorage.getItem('enterprise')) {
      this.selectedEnterprise = localStorage.getItem('enterprise') ?? '';
      const companyId = localStorage.getItem('company') ?? '';
      const submenu: any[] = [];

      forkJoin({
        config: this.enterpriseConfigService.getEnterpriseConfigById(this.selectedEnterprise).pipe(
          catchError(() => of(null))
        ),
        auxs: this.enterpriseConfigService.getAllAuxTableConfigs(this.selectedEnterprise).pipe(
          catchError(() => of([]))
        ),
        planUsage: companyId
          ? this.planUsageService.getPlanUsage(companyId).pipe(catchError(() => of(null)))
          : of(null)
      }).subscribe(({ config, auxs, planUsage }) => {
        // If config is null (API error), skip menu updates
        if (!config) {
          console.warn('Não foi possível carregar configuração da empresa. API pode estar indisponível.');
          return;
        }

        // Classifications submenu
        if (classificationsMenuIndex !== -1) {
          if (config.account) {
            submenu.push(this.createSubmenuItem('account'));
          }
          if (config.cost) {
            submenu.push(this.createSubmenuItem('cost'));
          }
          if (config.businessUnit) {
            submenu.push(this.createSubmenuItem('business-unit'));
          }
          if (config.local) {
            submenu.push(this.createSubmenuItem('local'));
          }
          auxs.forEach((item: { id: string, name: string; }) =>
            submenu.push(this.createSubmenuItem(item.name, item.id))
          );

          ROUTES[classificationsMenuIndex].submenu = submenu;
        }

        // Operations submenu - Show/hide Imports based on initial period + plan features
        if (operationsMenuIndex !== -1) {
          const isInInitialPeriod = config.initialPeriod === config.currentPeriod;
          this.updateOperationsSubmenu(operationsMenuIndex, isInInitialPeriod, planUsage);
        }

        // Export submenu - Show/hide Advanced Export based on plan feature
        if (exportMenuIndex !== -1) {
          this.updateExportSubmenu(exportMenuIndex, planUsage?.export ?? false);
        }

        // Reports submenu - Filter based on reportsAllTypes plan feature
        if (reportsMenuIndex !== -1) {
          this.updateReportsSubmenu(reportsMenuIndex, planUsage?.reportsAllTypes ?? false);
        }
      }, error => {
        console.error('Erro ao carregar itens do submenu', error);
        if (classificationsMenuIndex !== -1) {
          ROUTES[classificationsMenuIndex].submenu = [];
        }
      });
    } else {
      if (classificationsMenuIndex !== -1) {
        ROUTES[classificationsMenuIndex].submenu = [];
      }
    }
  }

  private updateOperationsSubmenu(operationsMenuIndex: number, isInInitialPeriod: boolean, planUsage?: import('@core/models/plan-usage.model').PlanUsage | null): void {
    const operationsMenu = ROUTES[operationsMenuIndex];
    if (!operationsMenu || !operationsMenu.submenu) return;

    // Remove or add imports menu item based on initial period
    const importsItemIndex = operationsMenu.submenu.findIndex(
      (item) => item.path === '/shared/operations/imports'
    );

    if (isInInitialPeriod) {
      // Add imports menu if not present
      if (importsItemIndex === -1) {
        operationsMenu.submenu.push({
          path: '/shared/operations/imports',
          title: 'MENUITEMS.OPERATION.IMPORTS',
          iconType: '',
          icon: '',
          class: 'ml-menu',
          groupTitle: false,
          badge: '',
          badgeClass: '',
          role: [UserRole.All],
          submenu: [],
        });
      }
    } else {
      // Remove imports menu if present
      if (importsItemIndex !== -1) {
        operationsMenu.submenu.splice(importsItemIndex, 1);
      }
    }

    // Show/hide Revaluations based on plan feature
    const canRevaluation = planUsage?.revaluation ?? true;
    const revaluationsIndex = operationsMenu.submenu.findIndex(
      (item) => item.path === '/enterprise/operations/revaluations'
    );
    if (!canRevaluation && revaluationsIndex !== -1) {
      operationsMenu.submenu.splice(revaluationsIndex, 1);
    }

    // Show/hide Impairments based on plan feature
    const canImpairment = planUsage?.impairment ?? true;
    const impairmentsIndex = operationsMenu.submenu.findIndex(
      (item) => item.path === '/enterprise/operations/impairments'
    );
    if (!canImpairment && impairmentsIndex !== -1) {
      operationsMenu.submenu.splice(impairmentsIndex, 1);
    }
  }

  private updateExportSubmenu(exportMenuIndex: number, exportEnabled: boolean): void {
    const exportMenu = ROUTES[exportMenuIndex];
    if (!exportMenu || !exportMenu.submenu) return;

    // Show/hide advanced export based on plan feature
    const advancedItemIndex = exportMenu.submenu.findIndex(
      (item) => item.path === '/enterprise/exports/advanced'
    );

    if (exportEnabled) {
      if (advancedItemIndex === -1) {
        exportMenu.submenu.push({
          path: '/enterprise/exports/advanced',
          title: 'MENUITEMS.EXPORT.ADVANCED',
          iconType: '',
          icon: '',
          class: 'ml-menu',
          groupTitle: false,
          badge: '',
          badgeClass: '',
          role: [UserRole.All],
          submenu: [],
        });
      }
    } else {
      if (advancedItemIndex !== -1) {
        exportMenu.submenu.splice(advancedItemIndex, 1);
      }
    }
  }

  private updateReportsSubmenu(reportsMenuIndex: number, reportsAllTypes: boolean): void {
    const reportsMenu = ROUTES[reportsMenuIndex];
    if (!reportsMenu || !reportsMenu.submenu) return;

    if (reportsAllTypes) {
      // Restore all report items if not already present
      const allReportPaths = [
        '/enterprise/reports/analytical',
        '/enterprise/reports/acquisitions',
        '/enterprise/reports/write-offs',
        '/enterprise/reports/transfers',
        '/enterprise/reports/total-depreciateds',
        '/enterprise/reports/balances-account',
        '/enterprise/reports/balances-cost',
        '/enterprise/reports/revaluations',
        '/enterprise/reports/impairments',
        '/enterprise/reports/intangibles',
      ];
      const allReportItems: RouteInfo[] = [
        { path: '/enterprise/reports/analytical', title: 'MENUITEMS.REPORT.ANALYTICAL', iconType: '', icon: '', class: 'ml-menu', groupTitle: false, badge: '', badgeClass: '', role: [UserRole.All], submenu: [] },
        { path: '/enterprise/reports/acquisitions', title: 'MENUITEMS.REPORT.ACQUISITIONS', iconType: '', icon: '', class: 'ml-menu', groupTitle: false, badge: '', badgeClass: '', role: [UserRole.All], submenu: [] },
        { path: '/enterprise/reports/write-offs', title: 'MENUITEMS.REPORT.WRITEOFFS', iconType: '', icon: '', class: 'ml-menu', groupTitle: false, badge: '', badgeClass: '', role: [UserRole.All], submenu: [] },
        { path: '/enterprise/reports/transfers', title: 'MENUITEMS.REPORT.TRANSFERS', iconType: '', icon: '', class: 'ml-menu', groupTitle: false, badge: '', badgeClass: '', role: [UserRole.All], submenu: [] },
        { path: '/enterprise/reports/total-depreciateds', title: 'MENUITEMS.REPORT.TOTAL-DEPRECIATED', iconType: '', icon: '', class: 'ml-menu', groupTitle: false, badge: '', badgeClass: '', role: [UserRole.All], submenu: [] },
        { path: '/enterprise/reports/balances-account', title: 'MENUITEMS.REPORT.BALANCES-ACCOUNT', iconType: '', icon: '', class: 'ml-menu', groupTitle: false, badge: '', badgeClass: '', role: [UserRole.All], submenu: [] },
        { path: '/enterprise/reports/balances-cost', title: 'MENUITEMS.REPORT.BALANCES-COST', iconType: '', icon: '', class: 'ml-menu', groupTitle: false, badge: '', badgeClass: '', role: [UserRole.All], submenu: [] },
        { path: '/enterprise/reports/revaluations', title: 'MENUITEMS.REPORT.REVALUATIONS', iconType: '', icon: '', class: 'ml-menu', groupTitle: false, badge: '', badgeClass: '', role: [UserRole.All], submenu: [] },
        { path: '/enterprise/reports/impairments', title: 'MENUITEMS.REPORT.IMPAIRMENTS', iconType: '', icon: '', class: 'ml-menu', groupTitle: false, badge: '', badgeClass: '', role: [UserRole.All], submenu: [] },
        { path: '/enterprise/reports/intangibles', title: 'MENUITEMS.REPORT.INTANGIBLES', iconType: '', icon: '', class: 'ml-menu', groupTitle: false, badge: '', badgeClass: '', role: [UserRole.All], submenu: [] },
      ];

      reportsMenu.submenu = allReportItems;
    } else {
      // Keep only the analytical report
      reportsMenu.submenu = reportsMenu.submenu.filter(
        (item) => item.path === '/enterprise/reports/analytical'
      );
    }
  }

  createSubmenuItem(name: string, id: string = ''): any {
    return {
      path: !id ? `/enterprise/classifications/${name}s` : `/enterprise/classifications/auxtables/${id}`,
      title: !id ? `MODELS.${name.toUpperCase()}.LIST` : name,
      iconType: '',
      icon: '',
      class: 'ml-menu',
      groupTitle: false,
      badge: '',
      badgeClass: '',
      role: [''],
      submenu: []
    };
  }

  initLeftSidebar() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this;
    // Set menu height
    _this.setMenuHeight();
    _this.checkStatuForResize(true);
  }
  setMenuHeight() {
    this.innerHeight = window.innerHeight;
    const height = this.innerHeight - this.headerHeight;
    this.listMaxHeight = height + '';
    this.listMaxWidth = '500px';
  }
  isOpen() {
    return this.bodyTag.classList.contains('overlay-open');
  }
  checkStatuForResize(firstTime: boolean) {
    if (window.innerWidth < 1025) {
      this.renderer.addClass(this.document.body, 'ls-closed');
    } else {
      this.renderer.removeClass(this.document.body, 'ls-closed');
    }
  }
  // Flyout submenu methods
  onMenuItemHover(event: Event, item: RouteInfo, index: number): void {
    this.checkCollapsedState();
    
    if (this.isCollapsed && item.submenu && item.submenu.length > 0 && !item.groupTitle) {
      const target = event.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      
      if (this.flyoutTimeout) {
        clearTimeout(this.flyoutTimeout);
      }
      this.flyoutItem = item;
      this.flyoutTop = rect.top;
      this.showFlyout = true;
    }
  }

  onMenuItemLeave(): void {
    this.flyoutTimeout = setTimeout(() => {
      this.closeFlyout();
    }, 200);
  }

  keepFlyoutOpen(): void {
    if (this.flyoutTimeout) {
      clearTimeout(this.flyoutTimeout);
    }
  }

  closeFlyout(): void {
    this.showFlyout = false;
    this.flyoutItem = null;
  }

  checkCollapsedState(): void {
    // Only update state outside of change detection cycle (from event handlers)
    this.updateCollapsedState();
  }

  // Tooltip methods for items without submenu
  getTooltipText(item: RouteInfo): string {
    return this.translateService.instant(item.title);
  }

  shouldShowTooltip(item: RouteInfo): boolean {
    // Don't call checkCollapsedState() here - use cached value to avoid NG0100 error
    return this.isCollapsed && item.submenu.length === 0 && !item.groupTitle;
  }
  logout() {
    this.subs.sink = this.authService.logout().subscribe((res) => {
      if (!res.success) {
        this.router.navigate(['/authentication/signin']);
      }
    });
  }
}
